import { AsyncValidatorFn, ValidatorError, ValidatorFn } from "./validation";

export abstract class FormControl<T, R> {
  constructor(
    private validators: ValidatorFn<T>[],
    private asyncValidators: AsyncValidatorFn<T>[],
  ) {
    this.value = null;
    this.rawValue = null;
  }

  public abstract syncFromElement(input: R): T;

  public abstract syncToElement(value: T): R;

  private value: T | null;
  private rawValue: R | null;

  private errors: ValidatorError[] = [];
  private asyncErrors: Promise<ValidatorError | null>[] = [];

  public setValue(value: T): void {
    this.value = value;
    this.rawValue = this.syncToElement(value);
    this.updateErrors();
  }

  private updateErrors() {
    this.errors = this.validators.reduce((accum, v) => {
      const er = v(this.value);
      if (er != null) {
        accum.push(er);
      }
      return accum;
    }, [] as ValidatorError[]);
    this.asyncErrors = this.asyncValidators.reduce(
      (accum, v) => {
        const er = v.validate(this.value);
        if (er != null) {
          accum.push(er);
        }
        return accum;
      },
      [] as Promise<ValidatorError | null>[],
    );
  }

  public getValue(): T | null {
    return this.value;
  }

  public setRawValue(rawValue: R): void {
    this.rawValue = rawValue;
    this.value = this.syncFromElement(rawValue);
    this.updateErrors();
  }

  public getRawValue(): R | null {
    return this.rawValue;
  }

  public getErrors(): ValidatorError[] {
    return this.errors;
  }
  public getAsyncErrors(): Promise<ValidatorError | null>[] {
    return this.asyncErrors;
  }

  public setValidators(validators: ValidatorFn<T>[]): void {
    this.validators = validators;
    this.updateErrors();
  }
}

export class TextFieldControl extends FormControl<string, string> {
  public syncFromElement(input: string): string {
    return input;
  }

  public syncToElement(value: string): string {
    return value;
  }
}

export class SelectFieldControl extends FormControl<string, string> {
  public syncFromElement(input: string): string {
    return input;
  }

  public syncToElement(value: string): string {
    return value;
  }
}

export class StaticForm<T extends { [key: string]: FormControl<any, any> }> {
  constructor(public controls: T) {}

  public getValue<K extends keyof T>(
    control: K,
  ): T[K] extends FormControl<infer V, infer _> ? V : never {
    return this.controls[control].getValue();
  }

  public getRawValue(control: keyof T): string | null {
    return this.controls[control].getRawValue();
  }

  // getValues
  public getValues(): {
    [K in keyof T]: T[K] extends FormControl<infer V, infer _> ? V : never;
  } {
    const values: Partial<{
      [K in keyof T]: T[K] extends FormControl<infer V, infer _> ? V : never;
    }> = {};
    for (const key in this.controls) {
      values[key] = this.controls[key].getValue();
    }
    // we've fetched all of them
    return values as {
      [K in keyof T]: T[K] extends FormControl<infer V, infer _> ? V : never;
    };
  }
}

export class DynamicForm {
  // untyped form controls since we need to create them reflexively based on the html
  constructor(public controls: { [key: string]: FormControl<any, any> }) {}

  public getValue(control: string): any {
    return this.controls[control];
  }

  public getRawValue(control: string): string | null {
    return this.controls[control].getRawValue();
  }

  // getValues
  public getValues(): { [key: string]: any } {
    const values: { [key: string]: any } = {};
    for (const key in this.controls) {
      values[key] = this.controls[key].getValue();
    }
    return values;
  }
  public addControl(key: string, control: FormControl<any, any>): void {
    this.controls = {
      ...this.controls,
      [key]: control,
    };
  }
}
